/**
 * dsCoderPacksService — scope-aware CRUD for DS Coder knowledge packs stored in
 * public.ds_coder_packs. RLS enforces who can read/write each scope; this layer
 * only shapes rows and sets the owner columns for the active scope.
 */
import { supabase, getCurrentUserId } from "@/lib/supabase";
import type { CommonError, GoldenLibrary } from "@/services/dsCoder/types";
import type { DsCoderPack, DsCoderPackInput, PackScope } from "@/types/dsCoderPacks";

const TABLE = "ds_coder_packs";
const SELECT =
  "id, scope, user_id, workspace_id, tech_id, label, docs_library_id, standards, common_errors, golden_libraries, is_enabled, created_at, updated_at";

type Row = {
  id: string;
  scope: PackScope;
  user_id: string | null;
  workspace_id: string | null;
  tech_id: string;
  label: string;
  docs_library_id: string | null;
  standards: unknown;
  common_errors: unknown;
  golden_libraries: unknown;
  is_enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
};

function toApp(row: Row): DsCoderPack {
  return {
    id: row.id,
    scope: row.scope,
    userId: row.user_id,
    workspaceId: row.workspace_id,
    techId: row.tech_id,
    label: row.label,
    docsLibraryId: row.docs_library_id,
    standards: Array.isArray(row.standards) ? (row.standards as string[]) : [],
    commonErrors: Array.isArray(row.common_errors) ? (row.common_errors as CommonError[]) : [],
    goldenLibraries: Array.isArray(row.golden_libraries) ? (row.golden_libraries as GoldenLibrary[]) : [],
    isEnabled: row.is_enabled,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Every pack the current user can apply for a chat: global defaults, their own
 * personal packs, and (when a workspace is active) that workspace's packs.
 * RLS guarantees only permitted rows return; we still filter workspace rows to
 * the active workspace client-side.
 */
export async function listResolvablePacks(workspaceId?: string | null): Promise<DsCoderPack[]> {
  const scopes: PackScope[] = workspaceId ? ["global", "user", "workspace"] : ["global", "user"];
  const { data, error } = await supabase.from(TABLE).select(SELECT).in("scope", scopes);
  if (error) throw new Error(error.message);
  return (data as Row[] | null ?? [])
    .filter((r) => (r.scope === "workspace" ? r.workspace_id === workspaceId : true))
    .map(toApp);
}

/** Packs for a single scope, for the management UI. */
export async function listPacks(params: {
  scope: PackScope;
  workspaceId?: string | null;
}): Promise<DsCoderPack[]> {
  let query = supabase.from(TABLE).select(SELECT).eq("scope", params.scope);
  if (params.scope === "workspace") {
    if (!params.workspaceId) return [];
    query = query.eq("workspace_id", params.workspaceId);
  } else if (params.scope === "user") {
    const userId = await getCurrentUserId();
    if (!userId) return [];
    query = query.eq("user_id", userId);
  }
  const { data, error } = await query.order("label", { ascending: true });
  if (error) throw new Error(error.message);
  return (data as Row[] | null ?? []).map(toApp);
}

export async function createPack(params: {
  scope: PackScope;
  workspaceId?: string | null;
  input: DsCoderPackInput;
}): Promise<DsCoderPack> {
  const { scope, workspaceId, input } = params;
  const userId = scope === "user" ? await getCurrentUserId() : null;
  if (scope === "user" && !userId) throw new Error("Not authenticated");
  if (scope === "workspace" && !workspaceId) throw new Error("A workspace must be selected");

  const row = {
    scope,
    user_id: scope === "user" ? userId : null,
    workspace_id: scope === "workspace" ? workspaceId : null,
    tech_id: input.techId.trim(),
    label: input.label.trim(),
    docs_library_id: input.docsLibraryId?.trim() || null,
    standards: input.standards,
    common_errors: input.commonErrors,
    golden_libraries: input.goldenLibraries,
    is_enabled: input.isEnabled ?? true,
  };
  const { data, error } = await supabase.from(TABLE).insert(row).select(SELECT).single();
  if (error) throw new Error(error.message);
  return toApp(data as Row);
}

export async function updatePack(
  id: string,
  patch: Partial<DsCoderPackInput>
): Promise<DsCoderPack> {
  const dbPatch: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.techId !== undefined) dbPatch.tech_id = patch.techId.trim();
  if (patch.label !== undefined) dbPatch.label = patch.label.trim();
  if (patch.docsLibraryId !== undefined) dbPatch.docs_library_id = patch.docsLibraryId?.trim() || null;
  if (patch.standards !== undefined) dbPatch.standards = patch.standards;
  if (patch.commonErrors !== undefined) dbPatch.common_errors = patch.commonErrors;
  if (patch.goldenLibraries !== undefined) dbPatch.golden_libraries = patch.goldenLibraries;
  if (patch.isEnabled !== undefined) dbPatch.is_enabled = patch.isEnabled;

  const { data, error } = await supabase
    .from(TABLE)
    .update(dbPatch)
    .eq("id", id)
    .select(SELECT)
    .single();
  if (error) throw new Error(error.message);
  return toApp(data as Row);
}

export async function deletePack(id: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq("id", id);
  if (error) throw new Error(error.message);
}

/** Whether the current user may edit GLOBAL packs. */
export async function isDsCoderAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc("is_ds_coder_admin");
  if (error) return false;
  return data === true;
}
